'use strict';

const { getArgon2, subtle } = require('./lib');

// Argon2id parameters. Tuned in the Phase 0 spike to ~0.5s in-browser at 64MB.
// Stored per-user (non-secret) so any device can re-derive on login.
const KDF = Object.freeze({ type: 'argon2id', time: 3, mem: 65536, parallelism: 1, hashLen: 32 });

// Domain-separation labels for the HKDF split. Versioned so we can rotate later.
const INFO_AUTH = 'web3keys/auth-verifier/v1';
const INFO_VAULT = 'web3keys/vault-key/v1';

/**
 * Stretch the password into a 32-byte master key with Argon2id.
 * The master key NEVER leaves the device and is never sent anywhere.
 */
async function deriveMaster(password, saltBytes) {
  const argon2 = getArgon2();
  const res = await argon2.hash({
    pass: password,
    salt: saltBytes, // Uint8Array
    type: argon2.ArgonType.Argon2id,
    time: KDF.time,
    mem: KDF.mem,
    parallelism: KDF.parallelism,
    hashLen: KDF.hashLen,
  });
  return new Uint8Array(res.hash); // 32 bytes
}

/**
 * Split the master key into two INDEPENDENT 32-byte outputs via HKDF-SHA256 with
 * distinct info labels:
 *   - authVerifier : proves login to the server (server only ever sees/stores this side).
 *   - vaultKey     : AES-256-GCM key for the vault (never leaves the device).
 * HKDF is one-way, so possessing authVerifier reveals NOTHING about vaultKey — this is
 * what lets the server authenticate users while remaining unable to derive their keys.
 */
async function hkdfSplit(masterBytes) {
  const s = subtle();
  const baseKey = await s.importKey('raw', masterBytes, 'HKDF', false, ['deriveBits']);
  const enc = (label) => new TextEncoder().encode(label);
  const noSalt = new Uint8Array(0);
  const authBits = await s.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: noSalt, info: enc(INFO_AUTH) }, baseKey, 256);
  const vaultBits = await s.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: noSalt, info: enc(INFO_VAULT) }, baseKey, 256);
  return { authVerifier: new Uint8Array(authBits), vaultKeyBytes: new Uint8Array(vaultBits) };
}

/**
 * Full client KDF: password + salt -> { authVerifier, vaultKeyBytes }.
 */
async function deriveKeyMaterial(password, saltBytes) {
  const master = await deriveMaster(password, saltBytes);
  return hkdfSplit(master);
}

module.exports = { KDF, deriveKeyMaterial };
